// Components
import CheckboxInput from "components/inputs/checkbox"
import DropdownInput from "components/inputs/dropdown"
import ListboxInput from 'components/inputs/listbox'
import ReportMfGrid from "components/property/reports/multifamily/report-mf-grid"
import { parseDate } from "data/lib/dates"
import propertyReportService from "data/reports-property"
// Data
import underwritingService from "data/underwriting"
import _ from 'lodash'
import calcService from "data/calc"
import React, { FC, useEffect, useState } from "react"
// Styles
import { ButtonWithPress_s, Button_s, Label_s } from "styles/app-styles"
import { InputNumber_s } from "styles/input-styles"
import { DateText_s, ReportForm_s } from "styles/report-styles"

interface UnitDetailProps {
  property: object
}

function useForceUpdate(){
  const [value, setValue] = useState(0); // integer state
  return () => setValue(value => value + 1); // update the state to force render
}

const UnitDetail: FC<UnitDetailProps> = (props) => {

  const amtTypeOptions = [
    { label: 'Amount', value: 'amount' },
    { label: 'Rollover Breakout', value: 'rollover_breakout'},
    { label: 'Per Area', value: 'per_area' },
    { label: 'Per Unit', value: 'per_unit' }
  ]
  const periodicityOptions = [
    { label: 'Annual', value: 12 },
    { label: 'Monthly', value: 1 },
    { label: 'Quarterly', value: 3 }
  ]
  const reportOptions = [
    {label: 'Occupied Area', value: 'occupied_area'},
    {label: 'Occupied Units', value: 'occupied_units'},
    {label: 'Vacant Area', value: 'vacant_area'},
    {label: 'Vacant Units', value: 'vacant_units'},
    {label: 'Market Rent', value: 'market_rent'},
    {label: 'Loss to Lease', value: 'loss_to_lease'},
    {label: 'Rent: Loss From Absorption/Turnover', value: 'vacant_rent'},
    {label: 'Rental Revenue', value: 'rent'},
    {label: 'Rent Abatements', value: 'abatement_rent'},
    {label: 'Scheduled Rental Revenue', value: 'scheduled_rent'},
    {label: 'Vacancy Loss', value: 'vacancy_loss'},
    {label: 'Credit Loss', value: 'credit_loss'},
    {label: 'Effective Rental Revenue', value: 'effective_rent'},
    {label: 'Make Ready', value: 'make_ready'},
    {label: 'Commissions', value: 'commissions'}
  ]

  const [amount_type, setAmtType] = useState<string>('amount');
  const [periodicity, setPeriodicity] = useState<number>(12)
  const [reports, setReports] = useState<any>(reportOptions.map(option => option.value));
  const [start_month, setStartMonth] = useState<string>(underwritingService.getReportBeginDefault(true, null, true))
  const [term, setTerm] = useState<number>(propertyReportService.getReportTermDefault())
  const [totals, setTotals] = useState<boolean>(false);
  const [unitTypes, setUnitTypes] = useState<number[]>([]);
  const forceUpdate = useForceUpdate();

  useEffect(() => {
    if (periodicity === 12 && totals === true){
      setTotals(false);
    }
  }, [periodicity])

  useEffect(() => {
    setStartMonth(underwritingService.getReportBeginDefault(true, null, true));
    setTerm(propertyReportService.getReportTermDefault());
  }, [props['property']])

  function submit():void {
    propertyReportService.propertyReport({
      report: 'unit_type_detail',
      params: {
        amount_type,
        period: periodicity,
        reports,
        term,
        start_month: underwritingService.toISOdate(start_month),
        totals,
        unitTypes: unitTypes.map(unit => unit['multifamilyid'])
      }
    })
  }

  function updateDate(date:string):void {
    const { adjustedDate, validatedTerm } = propertyReportService.updateDate(date, term);
    setStartMonth(adjustedDate);
    if (validatedTerm) setTerm(validatedTerm);
    forceUpdate();
  }

  function updateTerm(term:number):void {
    const adjustedTerm:number|boolean = propertyReportService.validateTerm(term, start_month);
    if (typeof adjustedTerm === 'number'){
      setTerm(adjustedTerm)
    } else {
      setTerm(term);
    }
    forceUpdate();
  }

  const disabled = unitTypes.length === 0 || !calcService.calcCheck(props.property)
  const buttonText = disabled ? unitTypes.length === 0 ? 'Unit Selection Required' : 'Calculation Required' : 'Run Report'

  return (
    <div>
      <ButtonWithPress_s primary onClick={submit} style={{marginBottom: 10}} disabled={disabled}>{buttonText}</ButtonWithPress_s>
      <ReportForm_s>
      <div>
          <Label_s>Start Date</Label_s>
          <DateText_s withoutDays value={parseDate(start_month).format('MM/DD/YYYY')} onChange={updateDate} onBlur={updateDate} persist={updateDate} />
        </div>
        <div>
          <Label_s>Term</Label_s>
          <InputNumber_s value={term} onChange={(e) => updateTerm(e.value)} maxFractionDigits={0} />
        </div>
        <div>
          <Label_s>Periodicity</Label_s>
          <DropdownInput
            persist={(field:string, val:number)=>setPeriodicity(val)}
            field="periodicity"
            options={periodicityOptions}
            value={periodicity}
          />
        </div>
        <div>
          <Label_s>Include Annual Totals</Label_s>
          <CheckboxInput
            disabled={periodicity === 12}
            field="totals"
            value={totals}
            persist={(field:string, val:boolean) => setTotals(val)}
          />
        </div>
        <div>
          <Label_s>Amount Type</Label_s>
          <DropdownInput
            persist={(field:string, val:string)=>setAmtType(val)}
            field="amount_type"
            options={amtTypeOptions}
            value={amount_type}
          />
        </div>
      </ReportForm_s>
      <ReportForm_s>
        <ReportMfGrid selected={unitTypes} setSelected={setUnitTypes} />
        <div>
          <div style={{display: 'flex', justifyContent: 'space-between', marginBottom: 10}}>
            <Label_s>Report Items</Label_s>
            <Button_s onClick={()=>{ reports.length === reportOptions.length ? setReports([]) : setReports(reportOptions.map(option => option.value))}}>
              {reports.length === reportOptions.length ? 'Deselect All' : 'Select All'}
            </Button_s>
          </div>
          <ListboxInput field="valuationPeriod" value={reports} options={reportOptions} persist={(field:string, val:string)=>setReports(val)} multiple={true} />
        </div>
      </ReportForm_s>
    </div>
  )
}

export default UnitDetail
